import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useAuth } from '../AuthContext';
import apiClient from '../lib/api-client';
import '../CSS/ProfileModal.css';

const ProfileModal = ({ show, onHide, onResetPassword, isResetting }) => {
  const { userData, userTier } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [subscriptions, setSubscriptions] = useState([]);
  const [regions, setRegions] = useState([]);

  // Add subscription form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [addScenario, setAddScenario] = useState('mean');
  const [isAdding, setIsAdding] = useState(false);

  // Edit subscription state
  const [editingRegionId, setEditingRegionId] = useState(null);
  const [editScenario, setEditScenario] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const handleEditClick = (sub) => {
    setEditingRegionId(sub.fema_region_id);
    setEditScenario(sub.default_report_scenario);
  };

  const handleEditCancel = () => {
    setEditingRegionId(null);
    setEditScenario('');
  };

  const handleEditSave = async (regionId) => {
    setIsUpdating(true);
    try {
      const updated = await apiClient.updatePdfSubscription(regionId, {
        is_subscribed: true,
        default_report_scenario: editScenario,
      });
      setSubscriptions(prev =>
        prev.map(s => s.fema_region_id === regionId ? updated : s)
      );
      setEditingRegionId(null);
      setEditScenario('');
    } catch (error) {
      console.error('Failed to update subscription:', error);
      alert(error.message || 'Failed to update. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (regionId) => {
    if (!window.confirm('Remove this region subscription?')) return;

    try {
      await apiClient.deletePdfSubscription(regionId);
      setSubscriptions(prev =>
        prev.filter(s => s.fema_region_id !== regionId)
      );
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      alert(error.message || 'Failed to remove. Please try again.');
    }
  };

  useEffect(() => {
    if (!show) {
      setProfileData(null);
      setActiveTab('profile');
      setSubscriptions([]);
      setShowAddForm(false);
      setSelectedRegions([]);
      return;
    }

    fetchData();
  }, [show, userData, activeTab]);

  const fetchData = async () => {
    try {
      const [details, mappings, regionList] = await Promise.all([
        apiClient.getUserDetails(),
        apiClient.getPdfSubscriptionMapping(),
        apiClient.getFEMARegions(),
      ]);
      setProfileData(details);
      setSubscriptions(mappings);
      setRegions(regionList);
    } catch (e) {
      console.error('Failed to fetch data', e);
      if (!profileData) setProfileData(userData);
    }
  };

  const navItems = [
    { id: 'profile', label: 'Profile Details', icon: 'fa-user' },
    { id: 'subscription', label: 'Email Updates', icon: 'fa-bell' },
  ];

  const getRegionName = (femaRegionId) => {
    const region = regions.find(r => r.id === femaRegionId);
    return region ? `Region ${region.id} — ${region.name}` : `Region ${femaRegionId}`;
  };

  const formatScenario = (scenario) => {
    switch (scenario) {
      case 'min': return 'Min';
      case 'mean': return 'Mean';
      case 'max': return 'Max';
      default: return scenario || 'N/A';
    }
  };

  // Regions not yet actively subscribed
  const availableRegions = regions.filter(r => {
    const activeSub = subscriptions.find(s => s.fema_region_id === r.id && s.is_subscribed);
    return !activeSub;
  });

  const toggleRegion = (id) => {
    setSelectedRegions(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const toggleAllRegions = () => {
    if (selectedRegions.length === availableRegions.length) {
      setSelectedRegions([]);
    } else {
      setSelectedRegions(availableRegions.map(r => r.id));
    }
  };

  const handleAddSubscription = async () => {
    if (selectedRegions.length === 0) return;

    setIsAdding(true);
    try {
      const response = await apiClient.addPdfSubscription({
        fema_region_ids: selectedRegions,
        default_report_scenario: addScenario,
        is_subscribed: true,
      });
      setSubscriptions(response);
      setShowAddForm(false);
      setSelectedRegions([]);
      setAddScenario('mean');
    } catch (error) {
      console.error('Failed to add subscription:', error);
      alert(error.message || 'Failed to add subscription. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  const renderProfileDetails = () => {
    if (!profileData) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading profile...</p>
        </div>
      );
    }

    return (
      <div className="profile-details-content">
        <h5 className="fw-bold mb-4">Profile Details</h5>

        <div className="profile-info-grid">
          <div className="detail-row">
            <span className="detail-label text-muted">First Name</span>
            <span className="detail-value">{profileData.first_name || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label text-muted">Last Name</span>
            <span className="detail-value">{profileData.last_name || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label text-muted">Email</span>
            <span className="detail-value">{profileData.email || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label text-muted">Company/Organisation</span>
            <span className="detail-value">{profileData.company_name || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label text-muted">Role</span>
            <span className="detail-value">{profileData.company_role || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label text-muted">Plan</span>
            <span className="detail-value">
              <span className={`badge px-3 py-1 ${userTier === 'pro' ? 'bg-warning text-dark' : 'bg-primary'}`}>
                {userTier === 'pro' ? (
                  <><i className="fas fa-crown me-1"></i>Pro</>
                ) : (
                  <><i className="fas fa-user me-1"></i>Free</>
                )}
              </span>
            </span>
          </div>
        </div>

        <hr className="my-4" />

        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={onResetPassword}
            disabled={isResetting}
            size="sm"
            className="px-3"
          >
            {isResetting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-key me-2"></i>
                Reset Password
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  const renderSubscriptionSettings = () => {
    const activeSubscriptions = subscriptions.filter(s => s.is_subscribed);

    return (
      <div className="subscription-settings-content">
        <h5 className="fw-bold mb-4">Email Updates</h5>

        <div className="mb-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <h6 className="text-muted mb-0">Promotional Emails</h6>
            <span className={`badge px-2 py-1 ${profileData?.is_promotional_subscribed ? 'bg-success' : 'bg-secondary'}`}>
              {profileData?.is_promotional_subscribed ? (
                <><i className="fas fa-check me-1"></i>Subscribed</>
              ) : (
                <><i className="fas fa-times me-1"></i>Not Subscribed</>
              )}
            </span>
          </div>
          <small className="text-muted">Receive performance details of forecasts and other updates.</small>
        </div>

        <hr />

        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              <h6 className="text-muted mb-0">FEMA Region Report Alerts</h6>
              {activeSubscriptions.length > 0 && (
                <span className="badge bg-success px-2 py-1">
                  <i className="fas fa-check me-1"></i>Active
                </span>
              )}
            </div>
            {!showAddForm && availableRegions.length > 0 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                <i className="fas fa-plus me-1"></i>Add
              </Button>
            )}
          </div>

          {/* Active subscriptions list */}
          {activeSubscriptions.length > 0 ? (
            <div className="border rounded p-2 mb-2">
              {activeSubscriptions.map((sub, idx) => (
                <div
                  key={sub.id}
                  className={`px-2 py-1 ${idx < activeSubscriptions.length - 1 ? 'border-bottom' : ''}`}
                >
                  {editingRegionId === sub.fema_region_id ? (
                    <div className="d-flex align-items-center gap-2">
                      <span className="small flex-shrink-0">{getRegionName(sub.fema_region_id)}</span>
                      <Form.Select
                        size="sm"
                        value={editScenario}
                        onChange={(e) => setEditScenario(e.target.value)}
                        style={{ maxWidth: '140px' }}
                      >
                        <option value="min">Min</option>
                        <option value="mean">Mean</option>
                        <option value="max">Max</option>
                      </Form.Select>
                      <button
                        className="btn btn-sm btn-outline-success p-0 px-1"
                        onClick={() => handleEditSave(sub.fema_region_id)}
                        disabled={isUpdating}
                        title="Save"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary p-0 px-1"
                        onClick={handleEditCancel}
                        disabled={isUpdating}
                        title="Cancel"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="small">
                        <span>{getRegionName(sub.fema_region_id)}</span>
                        <span className="text-muted ms-2">· {formatScenario(sub.default_report_scenario)}</span>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm p-1 text-muted border rounded"
                          onClick={() => handleEditClick(sub)}
                          title="Edit scenario"
                          style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          className="btn btn-sm p-1 text-muted border rounded"
                          onClick={() => handleDelete(sub.fema_region_id)}
                          title="Remove subscription"
                          style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <small className="text-muted d-block mb-2">You are not subscribed to any FEMA region alerts.</small>
          )}

          {/* Add subscription inline form */}
          {showAddForm && (
            <div className="border rounded p-3 mt-2" style={{ backgroundColor: 'var(--bg-hover)' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="fw-semibold small">Add Region Alerts</span>
                <button
                  type="button"
                  className="btn-close btn-close-sm"
                  onClick={() => { setShowAddForm(false); setSelectedRegions([]); }}
                  aria-label="Close"
                  style={{ fontSize: '0.6rem' }}
                ></button>
              </div>

              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Regions</Form.Label>
                <div className="border rounded p-2" style={{ maxHeight: '120px', overflowY: 'auto', backgroundColor: 'var(--bg-card)' }}>
                  <Form.Check
                    type="checkbox"
                    id="add-region-all"
                    label={<span className="small fw-semibold">All Available</span>}
                    checked={availableRegions.length > 0 && selectedRegions.length === availableRegions.length}
                    onChange={toggleAllRegions}
                    className="mb-1 pb-1 border-bottom"
                  />
                  {availableRegions.map((r) => (
                    <Form.Check
                      key={r.id}
                      type="checkbox"
                      id={`add-region-${r.id}`}
                      label={<span className="small">Region {r.id}{r.name ? ` — ${r.name}` : ''}</span>}
                      checked={selectedRegions.includes(r.id)}
                      onChange={() => toggleRegion(r.id)}
                      className="mb-1"
                    />
                  ))}
                </div>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Scenario</Form.Label>
                <Form.Select
                  size="sm"
                  value={addScenario}
                  onChange={(e) => setAddScenario(e.target.value)}
                >
                  <option value="min">Minimum (Best-case)</option>
                  <option value="mean">Mean (Expected)</option>
                  <option value="max">Maximum (Worst-case)</option>
                </Form.Select>
              </Form.Group>

              <Button
                variant="primary"
                size="sm"
                onClick={handleAddSubscription}
                disabled={isAdding || selectedRegions.length === 0}
                className="mt-1"
              >
                {isAdding ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus me-1"></i>
                    Add {selectedRegions.length > 0 ? `${selectedRegions.length} Region${selectedRegions.length !== 1 ? 's' : ''}` : ''}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="profile-modal">
      <Modal.Body className="p-0">
        <div className="profile-modal-layout">
          {/* Left sidebar nav */}
          <div className="profile-sidebar">
            <div className="sidebar-header">
              <div className="avatar-circle mx-auto mb-2">
                <span className="avatar-initials">
                  {profileData?.first_name ? profileData.first_name.charAt(0).toUpperCase()
                    : profileData?.email ? profileData.email.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              <small className="text-muted d-block text-center text-truncate px-2">
                {profileData?.email || ''}
              </small>
            </div>

            <nav className="sidebar-nav">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  className={`sidebar-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(item.id)}
                >
                  <i className={`fas ${item.icon} me-2`}></i>
                  {item.label}
                  <i className="fas fa-chevron-right ms-auto"></i>
                </button>
              ))}
            </nav>
          </div>

          {/* Right content area */}
          <div className="profile-content" key={activeTab}>
            <button
              className="btn-close position-absolute top-0 end-0 m-3"
              onClick={onHide}
              aria-label="Close"
            ></button>

            {activeTab === 'profile' && renderProfileDetails()}
            {activeTab === 'subscription' && renderSubscriptionSettings()}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProfileModal;
